import React, { useCallback } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { tagSetsToTags } from "@formant/core/common/tagSetsToTags";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IView } from "@formant/core/model/IView";
import { Uuid } from "@formant/core/model/Uuid";
import { defaultStoreId } from "@formant/rectangle-sdk";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Loader2,
    Skeleton,
} from "@formant/ui-kit";
import * as Menu from "@formant/ui/component/menu";
import { EditorMode, useBaseEditorStore } from "@formant/views-editor";

import { useRouter } from "../../../context/RouterContext";
import { useStores } from "../../../context/StoreContext";
import { authStore, routerStore } from "../../../stores";
import { copyView } from "../../../utils/copyView";
import { FeatureFlagLocalStorageManager } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagLocalStorageManager";
import { FeatureFlagsLocalStorageEnum } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagsLocalStorage.enum";
import { PopupPortal, usePopup } from "../../../utils/hooks/usePopupHooks";
import { viewConflictMessage } from "../../../utils/viewConflictMessage";
import useModuleMemory from "../../Observe/hooks/UseModuleMemory";
import { OverviewViewName } from "../../Overview/OverviewV2/OverviewV2";
import { getIconFromLayoutType } from "./F3.TabIcon";
import { TabItem } from "./F3.TabItem";
import { handleAnalyticsMigration } from "./utils/analytics-migration";
import { handleTelemetryMigration } from "./utils/telemetry-migration";

interface IConnectedTabItemProps {
    isStart: boolean;
    isEnd: boolean;
    view: DefinedEntity<IView>;
    isReorderable: boolean;
    isSaving: boolean;
    isSelected: boolean;
    filtering: boolean;
    deviceId?: Uuid;
    onToggleFilter?: () => void;
    onChangeViewId?: (id: Uuid) => void;
    onMoveToStart: (id: Uuid) => Promise<void>;
    onMoveToEnd: (id: Uuid) => Promise<void>;
    onMoveLeft: (id: Uuid) => Promise<void>;
    onMoveRight: (id: Uuid) => Promise<void>;
}

export const ConnectedTabItem = function ConnectedTabItem(
    props: IConnectedTabItemProps,
) {
    const {
        isStart,
        isEnd,
        view,
        isReorderable,
        isSaving,
        isSelected,
        filtering,
        deviceId,
        onChangeViewId,
        onMoveToStart,
        onMoveToEnd,
        onMoveLeft,
        onMoveRight,
    } = props;

    const { id } = view;
    const canConfigure = authStore.hasAccess("views", "administrator");

    const [migrationLoading, setMigrationLoading] = React.useState(false);

    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition: sortableTransition,
        isDragging,
    } = useSortable({
        id: view.id,
        disabled: !isReorderable || isSaving,
    });

    const transition = `${sortableTransition}, background-color 200ms ease-in-out 0ms, box-shadow 200ms ease-in-out 0ms`;
    const tabStyle: React.CSSProperties = transform
        ? {
              transform: CSS.Transform.toString(transform),
              transition,
              userSelect: "none",
          }
        : { transition, userSelect: "none" };

    const {
        isOpen,
        openDropdown: onOpenDropdown,
        closeDropdown: onCloseDropdown,
        setRefElement,
        setTargetElement,
        context: popperContext,
    } = usePopup<HTMLButtonElement, HTMLDivElement>({
        placement: "bottom-start",
        modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
    });

    const { navigate, routeTo } = useRouter();
    const { destroy } = useModuleMemory();
    const { appStore, observeStore, viewStore, groupStore } = useStores();

    const onSelect = useCallback(async () => {
        observeStore.configuringAnalyticsId = undefined;
        if (!deviceId && view?.id) {
            onChangeViewId?.(view.id);
            return;
        }

        if (deviceId) {
            await navigate(`/devices/${deviceId}/views/${view.id}`);
            return;
        }
    }, [onChangeViewId, navigate]);

    const findCloneName = (startName: string): string => {
        const list = viewStore.list();
        if (list?.some(_ => _.name === startName)) {
            return findCloneName(`${startName}-clone`);
        }
        return startName;
    };

    const onClone = useCallback(async () => {
        try {
            const saved = await viewStore.create({
                ...copyView(view),
                name: findCloneName(`${view.name}-copy`),
            });

            if (saved.id && deviceId) {
                fork(
                    routerStore.navigate(
                        routeTo.unifiedDevice({
                            mode: "views",
                            id: deviceId,
                            viewId: saved.id,
                        }),
                    ),
                );
                return;
            }

            if (onChangeViewId) {
                await onChangeViewId(saved.id);
            }
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            const { statusCode } = responseError;

            if (statusCode === 409) {
                appStore.setSnackbar(
                    viewConflictMessage(
                        responseError,
                        "A view with this name already exists",
                    ),
                );
            } else {
                throw error;
            }
        }
    }, [
        viewStore,
        findCloneName,
        fork,
        routerStore,
        routeTo,
        onChangeViewId,
        narrowError,
        appStore,
        findCloneName,
    ]);

    const onEmbed = useCallback(() => {
        if (view.layoutType === "dashboard") {
            observeStore.showEmbedModal = true;
            return;
        }
    }, [observeStore]);

    const onCustomize = useCallback(() => {
        if (
            view.layoutType === "analytics" ||
            view.layoutType === "dashboard"
        ) {
            observeStore.isCustomizing = true;
            useBaseEditorStore(defaultStoreId)
                ?.getState()
                .setMode(EditorMode.Edit);
            return;
        }
        fork(routerStore.navigate(routeTo.editView({ id: view.id })));
    }, [observeStore]);

    const viewId = view.id;
    const handleDelete = useCallback(() => {
        // START - hack to prevent deleting overview view
        if (view.name === OverviewViewName) {
            appStore.setSnackbar("Cannot delete overview view");
            return;
        }
        // END - hack to prevent deleting overview view
        appStore.setConfirm({
            title: "Delete dashboard",
            message: `Delete dashboard ${view.name}?`,
            onOk: async () => {
                const deletePromise = viewStore.delete(defined(viewId));
                if (view.smartFleetId) {
                    await navigate(
                        routeTo.smartFleet({ id: view.smartFleetId }),
                    );
                } else if (view.groupFilter?.tags) {
                    const groups = await groupStore.fetchList();
                    const groupTags = tagSetsToTags(view.groupFilter.tags);
                    // find group that matches tag
                    const group = groups.find(
                        _ =>
                            groupTags[_.tagKey] &&
                            groupTags[_.tagKey].includes(_.tagValue),
                    );
                    if (group?.id) {
                        await navigate(routeTo.smartFleet({ id: group.id }));
                    } else {
                        console.error("relevant group not found");
                        authStore.logout();
                    }
                } else if (deviceId) {
                    await navigate(
                        routeTo.device({
                            id: deviceId,
                        }),
                    );
                } else {
                    await navigate(routeTo.home());
                }

                await deletePromise;
                appStore.setSnackbar("View deleted");
            },
        });
    }, [appStore, navigate, routeTo, viewStore, viewId]);

    const handleMigration = async () => {
        setMigrationLoading(true);
        try {
            let newView: DefinedEntity<IView> | undefined;
            if (!view.layoutType || view.layoutType === "observe") {
                newView = await handleTelemetryMigration(view, deviceId);
            } else if (view.layoutType === "analytics") {
                newView = await handleAnalyticsMigration(view, deviceId);
            }

            if (newView) {
                if (deviceId) {
                    await navigate(`/devices/${deviceId}/views/${newView.id}`);
                } else if (onChangeViewId) {
                    onChangeViewId(newView.id);
                }
            }
        } catch (err) {
            console.error(err);
            appStore.setSnackbar(
                "Failed to convert this view. Please try again.",
            );
        } finally {
            setMigrationLoading(false);
        }
    };

    const handleResetLayout = useCallback(() => {
        if (deviceId) {
            destroy();
            window.location.reload();
        }
    }, [destroy, deviceId]);

    const handleMoveThisToStart = useCallback(
        () => onMoveToStart(id),
        [id, onMoveToStart],
    );
    const handleMoveThisToEnd = useCallback(
        () => onMoveToEnd(id),
        [id, onMoveToEnd],
    );
    const handleMoveThisLeft = useCallback(
        () => onMoveLeft(id),
        [id, onMoveLeft],
    );
    const handleMoveThisRight = useCallback(
        () => onMoveRight(id),
        [id, onMoveRight],
    );

    const reorderingMenu = isReorderable && (
        <>
            <Menu.Separator />
            <Menu.Item disabled={isStart} onClick={handleMoveThisToStart}>
                Move to start
            </Menu.Item>
            <Menu.Item disabled={isEnd} onClick={handleMoveThisToEnd}>
                Move to end
            </Menu.Item>
            <Menu.Item disabled={isStart} onClick={handleMoveThisLeft}>
                Move left
            </Menu.Item>
            <Menu.Item disabled={isEnd} onClick={handleMoveThisRight}>
                Move right
            </Menu.Item>
        </>
    );

    const extraMenu = {
        dashboard: null,
        observe: null,
        analytics: null,
        fullscreen: null,
        teleop: null,
    }[view.layoutType ?? "observe"];

    return (
        <>
            <TabItem
                filtering={filtering}
                icon={getIconFromLayoutType(view.layoutType ?? "observe")}
                isSaving={isSaving}
                isSelected={isSelected}
                ref={setNodeRef}
                dropdownRef={setRefElement}
                onSelect={onSelect}
                onOpenDropdown={canConfigure ? onOpenDropdown : undefined}
                style={tabStyle}
                isDragging={isDragging}
                {...attributes}
                {...listeners}
            >
                {view.name}
            </TabItem>
            <PopupPortal isOpen={isOpen} context={popperContext}>
                <Menu.Container
                    ref={setTargetElement}
                    onItemClicked={onCloseDropdown}
                    style={{ maxHeight: "calc(100vh - 75px)" }}
                >
                    {view.layoutType !== "analytics" &&
                        view.layoutType !== "dashboard" && (
                            <Menu.Item
                                onClick={handleResetLayout}
                                launcher={true}
                            >
                                Reset Layout
                            </Menu.Item>
                        )}
                    <Menu.Item onClick={onCustomize} launcher={true}>
                        Edit
                    </Menu.Item>
                    {view.layoutType !== "dashboard" &&
                        view.layoutType !== "teleop" &&
                        FeatureFlagLocalStorageManager.getFlag(
                            FeatureFlagsLocalStorageEnum.EnableV2Migration,
                        ) && (
                            <Menu.Item
                                onClick={handleMigration}
                                launcher={true}
                            >
                                Convert to Coherence
                            </Menu.Item>
                        )}

                    {view.layoutType === "dashboard" && (
                        <Menu.Item onClick={onEmbed} launcher={true}>
                            Exbed
                        </Menu.Item>
                    )}

                    {(view.layoutType === "dashboard" ||
                        view.layoutType === "analytics") && (
                        <Menu.Item onClick={onClone} launcher={false}>
                            Duplicate
                        </Menu.Item>
                    )}
                    <Menu.Item onClick={handleDelete} launcher={true}>
                        Delete
                    </Menu.Item>
                    {reorderingMenu}
                    {extraMenu}
                </Menu.Container>
            </PopupPortal>
            <Dialog
                open={migrationLoading}
                onOpenChange={open => {
                    // Only allow opening, not closing
                    if (open === false) {
                        return;
                    }
                    setMigrationLoading(false);
                }}
            >
                <DialogContent
                    className="sm:max-w-md flex flex-col items-center justify-center p-6 gap-6"
                    onEscapeKeyDown={e => {
                        // Prevent closing with ESC key
                        e.preventDefault();
                    }}
                    onPointerDownOutside={e => {
                        // Prevent closing when clicking outside
                        e.preventDefault();
                    }}
                    onInteractOutside={e => {
                        // Prevent any interaction outside
                        e.preventDefault();
                    }}
                >
                    <DialogTitle>Migrating content from v1 to v2</DialogTitle>
                    <div className="flex flex-col items-center text-center space-y-4">
                        <Loader2 className="h-12 w-12 text-primary animate-spin" />
                        <p className="text-sm text-muted-foreground">
                            Please wait while we do migration from v1 to v2.
                            This process may take a moment.
                        </p>
                    </div>

                    <div className="w-full space-y-2">
                        <Skeleton className="h-2 w-full bg-muted overflow-hidden rounded-ful" />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
